import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { RegistryLineItemInput } from '@graphql/generated-base';
import { useCreateRegistryPurchaseContextMutation } from '@graphql/mutations/order.graphql.generated';
import { withWindow } from '@shared/utils/withWindow';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';

import { useShoppingCart } from '@apps/registry/common/components/ShoppingCart/state';
import type { LineItem } from '../../../ShoppingCart.types';
import { CartCheckoutSteps, ShippingAddressOptions } from '../../../ShoppingCart.types';
import { StyledLoader, StyledLoadingWrapper, styles } from './PaymentStep.styles';
import { useTheme } from '@withjoy/joykit';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { PaymentErrorScreen } from './PaymentErrorScreen';
import { CheckoutFailureCode } from '../../../utils/constants';
import { ReactComponent as EmptyCartIcon } from '@apps/registry/admin/assets/empty-cart.svg';
import { ReactComponent as MissingGift } from '@apps/registry/admin/assets/missing-gift.svg';
import { ReactComponent as BadAddress } from '@apps/registry/admin/assets/Shipping.svg';
import { shippingAddressFragmentAsInput } from '@apps/registry/common/utils/shippingAddress';
import { deriveAggregateCheckoutCodesResult } from '@apps/registry/common/utils/checkoutMechanisms';
import { composePurchaseReturnUrlTemplate, usePurchaseConfirmationLink } from '@apps/registry/guest/hooks/usePurchaseConfirmationLink';

const formatList = (itemList: Array<LineItem>): Array<RegistryLineItemInput> => itemList.map(item => ({ quantity: item.quantity, registryItemId: item.registryItemId }));

type PaymentStepProps = {
  eventId: string;
  eventHandle: string;
};

export const PaymentStep = ({ eventId, eventHandle }: PaymentStepProps) => {
  const {
    state: { itemList, shippingAddress, details, hadOnlyStillNeededItems, selectedShippingOption, lastSyncAt, isSyncing },
    mutators: { updateCurrentCheckoutStep, syncItems },
  } = useShoppingCart();
  const routes = useRoutePaths();
  const { colors } = useTheme();
  const [createPurchaseContext] = useCreateRegistryPurchaseContextMutation();
  const isCreatingContextRef = useRef(false);
  const hasNotNeededError = !hadOnlyStillNeededItems;
  const [hasCheckoutError, setHasCheckoutError] = useState('');
  // Track error in a ref so the useEffect can read it without re-triggering.
  // This breaks the re-render loop where setting hasCheckoutError (state)
  // re-fires the effect via its dependency array, which re-calls the mutation
  // before the error screen renders.
  const checkoutErrorRef = useRef('');
  const { t2 } = useTranslation('sharedRegistry');
  const { checkoutErrorTitle, checkoutErrorAction, inventoryLow, badAddress, registryError, checkoutErrorText } = t2('shoppingCart', 'paymentStep');
  const getSuccessUrl = usePurchaseConfirmationLink(eventHandle);

  const analytics = useContext(AnalyticsContext);

  const handleCheckoutError = useCallback(
    (checkoutError?: string) => {
      const errorValue = checkoutError || CheckoutFailureCode.SERVER_ERROR;
      checkoutErrorRef.current = errorValue;
      setHasCheckoutError(errorValue);
      analytics?.track({
        category: 'guest.registry',
        action: 'CheckoutFailed',
        extraInfo: {
          error: errorValue,
        },
      });
    },
    [analytics],
  );

  useEffect(
    () =>
      withWindow(
        global => {
          const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
              updateCurrentCheckoutStep(CartCheckoutSteps.Cart);
            }
          };
          global.addEventListener('pageshow', handlePageShow);
          return () => global.removeEventListener('pageshow', handlePageShow);
        },
        () => undefined,
      ),
    [updateCurrentCheckoutStep],
  );

  useEffect(() => {
    if (!shippingAddress || !details || !details.email || isSyncing) {
      return;
    }

    // if cart was synced more than a minute ago
    if (Date.now() - (lastSyncAt?.getTime() ?? 0) > 60 * 1000) {
      syncItems();
      return;
    }

    if (hasNotNeededError || checkoutErrorRef.current) {
      if (!hasCheckoutError) {
        handleCheckoutError(checkoutErrorRef.current);
      }
      return;
    }

    if (isCreatingContextRef.current) {
      return;
    }

    (async () => {
      isCreatingContextRef.current = true;
      try {
        const failureUrlTemplate = withWindow(global => global.location.origin + routes.guestRegistry.path, '');
        const baseSuccessRoute = withWindow(global => global.location.origin + routes.purchase.path, '');
        const baseSuccessUrl = composePurchaseReturnUrlTemplate(baseSuccessRoute);
        const successUrlTemplate = getSuccessUrl(baseSuccessUrl);
        const purchaseContext = await createPurchaseContext({
          variables: {
            payload: {
              eventId: eventId!,
              name: details.name,
              email: details.email,
              note: details.message,
              lineItems: formatList(itemList),
              failureUrlTemplate,
              successUrlTemplate,
              address: selectedShippingOption === ShippingAddressOptions.CoupleAddress ? undefined : shippingAddressFragmentAsInput(shippingAddress),
              eCard: details?.eCard ?? undefined,
            },
          },
        });
        const createRegistryPurchaseContext = purchaseContext?.data?.createRegistryPurchaseContext ?? null;
        const checkoutUrl = createRegistryPurchaseContext?.checkoutUrl;
        const { checkoutFailureCode } = deriveAggregateCheckoutCodesResult([createRegistryPurchaseContext]);

        if (checkoutUrl && !checkoutFailureCode) {
          withWindow(global => (global.location.href = checkoutUrl!));
        } else {
          isCreatingContextRef.current = false;
          handleCheckoutError(checkoutFailureCode ?? undefined);
        }
      } catch (err) {
        isCreatingContextRef.current = false;
        // truly unknown, when present
        const code = (err as { code?: string } | null | undefined)?.code;
        const name = (err as object | null | undefined)?.constructor.name;
        handleCheckoutError(code ?? name ?? undefined);
      }
    })();
  }, [
    itemList,
    shippingAddress,
    details,
    createPurchaseContext,
    eventId,
    routes,
    hasNotNeededError,
    selectedShippingOption,
    syncItems,
    lastSyncAt,
    eventHandle,
    getSuccessUrl,
    isSyncing,
    handleCheckoutError,
    hasCheckoutError,
  ]);

  if (hasCheckoutError) {
    let errorMessage;
    let checkoutErrorIcon;
    switch (hasCheckoutError) {
      case CheckoutFailureCode.INVENTORY_LOW:
        errorMessage = inventoryLow;
        checkoutErrorIcon = <MissingGift />;
        break;
      case CheckoutFailureCode.BAD_ADDRESS:
        errorMessage = badAddress;
        checkoutErrorIcon = <BadAddress />;
        break;
      case CheckoutFailureCode.REGISTRY_ERROR:
        errorMessage = registryError;
        checkoutErrorIcon = <EmptyCartIcon />;
        break;
      default:
        errorMessage = checkoutErrorText;
        checkoutErrorIcon = <EmptyCartIcon />;
    }
    return (
      <PaymentErrorScreen
        icon={checkoutErrorIcon}
        title={checkoutErrorTitle}
        text={errorMessage}
        action={checkoutErrorAction}
        handleAction={() => {
          updateCurrentCheckoutStep(CartCheckoutSteps.Cart);
        }}
      />
    );
  }

  return (
    <StyledLoadingWrapper __css={styles.loadingWrapperStyles}>
      <svg
        width={52}
        height={52}
        x="0px"
        y="0px"
        viewBox="0 0 52 52"
      >
        <circle
          strokeWidth={4}
          strokeLinecap="round"
          r={18}
          cx={26}
          cy={26}
          stroke={colors.mono4}
          fill="transparent"
        />
        <StyledLoader
          strokeWidth={4}
          strokeLinecap="round"
          r={18}
          cx={26}
          cy={26}
        />
      </svg>
    </StyledLoadingWrapper>
  );
};
